import {
  CheckCircleOutlined,
  DeleteOutlined,
  DownOutlined,
  SendOutlined,
  UndoOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { Button, Space, Tag, Tooltip, Typography } from 'antd'
import { RichTextEditorLexical } from 'features/richText/components/RichTextEditorLexical'
import { RichTextViewer } from 'features/richText/components/RichTextViewer'
import { AlertsMentionPlugin } from 'features/richText/editorPlugins/AlertsMentionPlugin'
import { FC, useCallback, useState } from 'react'
import styled from 'styled-components'
import { extractPlainText, RichTextContent } from 'types'

import { type AnchorLabelContext, getAnchorLabel } from './anchorLabels'
import type { CommentThread } from './commentTypes'

export interface CommentBubbleProps {
  thread: CommentThread
  labelCtx: AnchorLabelContext
  isExpanded: boolean
  isFocused: boolean
  isOutdated: boolean
  onExpand: () => void
  onCollapse: () => void
  onReply: (text: RichTextContent) => void
  onResolve: () => void
  onReopen: () => void
  onDelete: () => void
  onDeleteComment: (commentId: string) => void
  currentUserId: string
}

export const CommentBubble: FC<CommentBubbleProps> = ({
  thread,
  labelCtx,
  isExpanded,
  isFocused,
  isOutdated,
  onExpand,
  onCollapse,
  onReply,
  onResolve,
  onReopen,
  onDelete,
  onDeleteComment,
  currentUserId,
}) => {
  const [replyText, setReplyText] = useState<RichTextContent>(
    '' as RichTextContent
  )
  const [replyEditorKey, setReplyEditorKey] = useState(0)
  const firstComment = thread.comments[0]
  const isAuthor = firstComment?.author === currentUserId
  const replyCount = thread.comments.length - 1

  const handleReply = useCallback(() => {
    if (!extractPlainText(replyText).trim()) return
    onReply(replyText)
    setReplyText('' as RichTextContent)
    setReplyEditorKey((k) => k + 1)
  }, [onReply, replyText])

  return (
    <BubbleCard
      $isFocused={isFocused}
      $isResolved={thread.resolved}
      $isExpanded={isExpanded}>
      <CollapsedSection onClick={isExpanded ? onCollapse : onExpand}>
        {!isExpanded && (
          <CommentPreview ellipsis={{ rows: 2 }}>
            {extractPlainText(firstComment?.text as RichTextContent)}
          </CommentPreview>
        )}
        <CollapsedHeader>
          <CollapsedByline>
            <AuthorName>
              {firstComment?.authorName ?? firstComment?.author}
            </AuthorName>
            {!isExpanded && firstComment && (
              <TimestampText>
                {new Date(firstComment.createdAt).toLocaleString()}
              </TimestampText>
            )}
          </CollapsedByline>
          <CollapsedMeta>
            {isOutdated && <MiniTag color='orange'>Outdated</MiniTag>}
            {thread.resolved && <MiniTag color='green'>Resolved</MiniTag>}
            {!isExpanded && replyCount > 0 && (
              <ReplyCount>
                {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
              </ReplyCount>
            )}
            <ToggleIcon $expanded={isExpanded} />
          </CollapsedMeta>
        </CollapsedHeader>
      </CollapsedSection>

      <ExpandableWrapper $expanded={isExpanded}>
        <ExpandableClip>
          <ExpandableInner>
            <ExpandedHeader>
              <AnchorLabel>
                {getAnchorLabel(thread.anchor, labelCtx)}
              </AnchorLabel>
              <Space size={4}>
                {thread.resolved ? (
                  <Tooltip title='Reopen'>
                    <Button
                      size='small'
                      icon={<UndoOutlined />}
                      onClick={onReopen}
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title='Resolve'>
                    <Button
                      size='small'
                      type='primary'
                      icon={<CheckCircleOutlined />}
                      onClick={onResolve}
                    />
                  </Tooltip>
                )}
                {isAuthor && (
                  <Tooltip title='Delete thread'>
                    <Button
                      size='small'
                      danger
                      icon={<DeleteOutlined />}
                      onClick={onDelete}
                    />
                  </Tooltip>
                )}
              </Space>
            </ExpandedHeader>

            {isOutdated && (
              <OutdatedBanner>
                <WarningOutlined />
                <span>
                  This comment may be outdated — the field value has changed
                  since this comment was created.
                </span>
              </OutdatedBanner>
            )}

            <CommentsContainer>
              {thread.comments.map((comment, idx) => (
                <CommentEntry key={comment.id}>
                  <CommentEntryHeader>
                    <Space size={4}>
                      <CommentAuthor>
                        {comment.authorName ?? comment.author}
                      </CommentAuthor>
                      <TimestampText>
                        {new Date(comment.createdAt).toLocaleString()}
                      </TimestampText>
                    </Space>
                    {idx > 0 && comment.author === currentUserId && (
                      <Tooltip title='Delete reply'>
                        <DeleteReplyButton
                          size='small'
                          type='text'
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => onDeleteComment(comment.id)}
                        />
                      </Tooltip>
                    )}
                  </CommentEntryHeader>
                  <CommentBody>
                    <RichTextViewer
                      content={comment.text as RichTextContent}
                      emptyMessage=''
                    />
                  </CommentBody>
                </CommentEntry>
              ))}
            </CommentsContainer>

            {!thread.resolved && (
              <ReplyBox>
                <RichTextEditorLexical
                  key={replyEditorKey}
                  value={replyText}
                  onChange={setReplyText}
                  placeholder='Write a reply...'
                  mentionPlugin={<AlertsMentionPlugin />}
                  enableSlashCommands={false}
                />
                <Button
                  type='primary'
                  size='small'
                  icon={<SendOutlined />}
                  disabled={!extractPlainText(replyText).trim()}
                  onClick={handleReply}>
                  Reply
                </Button>
              </ReplyBox>
            )}

            {thread.resolved && thread.resolvedBy && (
              <ResolvedFooter>
                <CheckCircleOutlined /> Resolved by{' '}
                {thread.resolvedByName ?? thread.resolvedBy}
              </ResolvedFooter>
            )}
          </ExpandableInner>
        </ExpandableClip>
      </ExpandableWrapper>
    </BubbleCard>
  )
}

export const NewCommentBubble: FC<{
  anchorLabel: string
  onSubmit: (text: RichTextContent) => void
  onCancel: () => void
}> = ({ anchorLabel, onSubmit, onCancel }) => {
  const [text, setText] = useState<RichTextContent>('' as RichTextContent)

  const handleSubmit = useCallback(() => {
    if (!extractPlainText(text).trim()) return
    onSubmit(text)
    setText('' as RichTextContent)
  }, [onSubmit, text])

  return (
    <NewCommentCard>
      <AnchorLabel>{anchorLabel}</AnchorLabel>
      <RichTextEditorLexical
        value={text}
        onChange={setText}
        placeholder='Write a comment...'
        mentionPlugin={<AlertsMentionPlugin />}
        enableSlashCommands={false}
        autoFocus={true}
      />
      <ButtonRow>
        <Button size='small' onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type='primary'
          size='small'
          icon={<SendOutlined />}
          disabled={!extractPlainText(text).trim()}
          onClick={handleSubmit}>
          Comment
        </Button>
      </ButtonRow>
    </NewCommentCard>
  )
}

const SIDEBAR_BUBBLE_BORDER_LEFT = '3px'

const BubbleCard = styled.div<{
  $isFocused: boolean
  $isResolved: boolean
  $isExpanded: boolean
}>`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  width: 100%;
  overflow: hidden;
  background: ${({ theme }) => theme.forecast.background2};
  border: 1px solid
    ${({ $isFocused, theme }) =>
      $isFocused ? theme.forecast.primary : theme.forecast.border};
  border-left: ${SIDEBAR_BUBBLE_BORDER_LEFT} solid
    ${({ $isResolved, $isFocused, theme }) =>
      $isFocused
        ? theme.forecast.primary
        : $isResolved
          ? theme.common.badge.text.green
          : theme.forecast.primary};
  opacity: ${({ $isResolved, $isExpanded }) =>
    $isResolved && !$isExpanded ? 0.7 : 1};
  box-shadow: ${({ $isExpanded }) =>
    $isExpanded ? '0 2px 12px rgba(0, 0, 0, 0.1)' : 'none'};
  transition:
    border-color 0.2s,
    box-shadow 0.2s,
    opacity 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.forecast.primary};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`

const CollapsedSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  cursor: pointer;
`

const ExpandableWrapper = styled.div<{ $expanded: boolean }>`
  display: grid;
  grid-template-rows: ${({ $expanded }) => ($expanded ? '1fr' : '0fr')};
  transition: grid-template-rows 0.25s ease;
`

const ExpandableClip = styled.div`
  overflow: hidden;
  min-height: 0;
`

const ExpandableInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 12px 12px;
`

const CollapsedHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`

const CollapsedByline = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`

const CollapsedMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`

const ExpandedHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`

const ToggleIcon = styled(DownOutlined)<{ $expanded: boolean }>`
  &&& {
    font-size: 10px;
    color: ${({ theme }) => theme.forecast.secondaryText};
    transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});
    transition: transform 0.25s ease;
  }
`

const AuthorName = styled(Typography.Text).attrs({ strong: true })`
  &&& {
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const CommentPreview = styled(Typography.Paragraph)`
  &&& {
    margin: 0;
    font-size: 12px;
    color: ${({ theme }) => theme.forecast.defaultText};
  }
`

const ReplyCount = styled(Typography.Text).attrs({ type: 'secondary' })`
  &&& {
    font-size: 11px;
  }
`

const MiniTag = styled(Tag)`
  &&& {
    margin: 0;
    font-size: 10px;
    line-height: 18px;
    padding: 0 4px;
  }
`

const AnchorLabel = styled(Typography.Text).attrs({ type: 'secondary' })`
  &&& {
    font-size: 11px;
  }
`

const CommentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 300px;
  overflow-y: auto;
`

const CommentEntry = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 8px;
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.background1};
  border: 1px solid ${({ theme }) => theme.forecast.border};
`

const CommentEntryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const DeleteReplyButton = styled(Button)`
  &&& {
    width: 20px;
    height: 20px;
    min-width: 20px;
    font-size: 10px;
    opacity: 0.5;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
    }
  }
`

const CommentAuthor = styled(Typography.Text).attrs({ strong: true })`
  &&& {
    font-size: 12px;
  }
`

const TimestampText = styled(Typography.Text).attrs({ type: 'secondary' })`
  &&& {
    font-size: 10px;
  }
`

const CommentBody = styled(Typography.Paragraph)`
  &&& {
    margin: 0;
    font-size: 12px;
  }
`

const OutdatedBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 11px;
  color: ${({ theme }) => theme.forecast.warning};
  background: rgba(250, 173, 20, 0.08);
  border: 1px solid ${({ theme }) => theme.forecast.warning};
`

const ReplyBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const ResolvedFooter = styled(Typography.Text).attrs({ type: 'success' })`
  &&& {
    font-size: 11px;
  }
`

const NewCommentCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.forecast.primary};
  border-left: ${SIDEBAR_BUBBLE_BORDER_LEFT} solid
    ${({ theme }) => theme.forecast.primary};
  background: ${({ theme }) => theme.forecast.blueGlassBackground};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  width: 100%;
`

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
`
